<template>
  <div>
    <h1 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-none md:italic md:font-serif md:tracking-tiny mb-5 uppercase text-center md:text-left md:normal-case font-bold tracking-lg">
      {{ $t('Order History') }}
    </h1>
    <div class="bg-white lg:py-xl lg:px-8.5 p-md">
      <div class="lg:flex mb-sm justify-between">
        <div v-if="!isLoading && orders.length" class="mb-md lg:mb-0">
          <span>{{ `Items ${from}` }}</span>
          <span>{{ ` to ${to}` }}</span>
        </div>
        <div v-else-if="isLoading" class="mb-md lg:mb-0 skeleton-row items-center hidden lg:flex">
          <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-md" />
        </div>
        <div class="lg:flex items-center">
          <span class="mr-sm text-h4 leading-base tracking-mid hidden lg:inline">{{ $t('Show') }}</span>
          <base-select
            name="showBy"
            :options="showBy"
            input-class="uppercase order-pagination__select"
            caret-size="icon-xs"
            :selected="String(perPage)"
            v-model="perPage"
          />
          <span class="ml-sm text-h4 leading-base tracking-mid hidden lg:inline">{{ $t('per page') }}</span>
        </div>
      </div>

      <div class="flex row items-center leading-none py-4 px-2 justify-center mb-md">
        <a
          href="#"
          class="mr-sm no-underline text-grey-32 hover:text-black"
          :class="{ 'opacity-25': currentPage === 1 || isLoading }"
          @click.prevent="currentPage === 1 || isLoading ? null : prevPage()"
        >
          <svg class="icon-lg text-black fill-current">
            <use xlink:href="/assets/icons.svg#full-arrow-left" />
          </svg>
          <span class="inline-block text-black text-md mr-md">{{ $t('Newer') }}</span>
        </a>
        <span class="inline-block text-black mr-sm text-md">{{ currentPage }}</span>
        <a
          href="#"
          class="no-underline"
          :class="{ 'opacity-25': !hasMoreOrders || isLoading }"
          @click.prevent="!hasMoreOrders || isLoading ? null : nextPage()"
        >
          <span class="inline-block text-black mr-sm ml-md text-md">{{ $t('Older') }}</span>
          <svg class="icon-lg text-black fill-current">
            <use xlink:href="/assets/icons.svg#full-arrow-right" />
          </svg>
        </a>
      </div>

      <table class="w-full my-10">
        <thead>
          <tr>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm">
              {{ $t('ORDER #') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden sm:table-cell">
              {{ $t('DATE') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('TYPE') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('OPENING ORDER') }}
            </th>
            <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('ORDER TOTAL') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr
            v-for="n in skeletonRowCount"
            :key="`skeleton-${n}`"
            class="skeleton-row"
          >
            <td class="pt-sm pb-xs">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-sm" />
            </td>
            <td class="pt-sm pb-xs hidden sm:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-md" />
            </td>
            <td class="pt-sm pb-xs hidden md:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-lg" />
            </td>
            <td class="pt-sm pb-xs hidden md:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xs" />
            </td>
            <td class="pt-sm pb-xs text-right hidden md:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xs ml-auto" />
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!orders.length">
          <tr>
            <td class="w-1/2 py-2" colspan="5">
              <p class="leading-base tracking-mid mb-md my-3">
                {{ $t('Looks like you haven\'t placed any orders.') }}
              </p>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="order in orders"
            :key="order.id"
          >
            <td class="pt-sm">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ order.etail_id || order.id }}
              </router-link>
            </td>
            <td class="pt-sm hidden sm:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ order.order_date | localeDate }}
              </router-link>
            </td>
            <td class="pt-sm hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ formatOrderType(order.transaction_type) }}
              </router-link>
            </td>
            <td class="pt-sm hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ isOpeningOrder(order) ? $t('Yes') : $t('No') }}
              </router-link>
            </td>
            <td class="pt-sm text-right hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="text-base leading-sm tracking-xs text-dark italic font-serif underline hover:text-primary2 slow-color-change"
              >
                {{ Number(order.order_total || 0) | price }}
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import debounce from 'lodash-es/debounce'
import BaseSelect from 'theme/components/core/blocks/Form/BaseSelect'
import { localeDate } from 'theme/filters/locale-date'

export default {
  name: 'NetsuiteOrders',
  components: {
    BaseSelect
  },
  filters: {
    localeDate
  },
  data () {
    return {
      currentPage: 1,
      perPage: 25,
      skeletonRowCount: 8,
      showBy: [
        { label: '25', value: '25' },
        { label: '50', value: '50' },
        { label: '100', value: '100' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      orders: 'netsuite/getOrderSummaries',
      pagination: 'netsuite/getOrderSummariesPagination',
      isLoading: 'netsuite/isLoading'
    }),
    from () {
      if (!this.orders.length) return 0
      return ((this.currentPage - 1) * Number(this.perPage)) + 1
    },
    to () {
      if (!this.orders.length) return 0
      return ((this.currentPage - 1) * Number(this.perPage)) + this.orders.length
    },
    hasMoreOrders () {
      return this.orders.length >= Number(this.perPage)
    }
  },
  watch: {
    perPage () {
      this.currentPage = 1
      this.fetchPage()
    }
  },
  mounted () {
    this.fetchPage()
  },
  methods: {
    fetchPage: debounce(function () {
      this.$store.dispatch('netsuite/fetchOrderSummaries', {
        limit: Number(this.perPage),
        page: this.currentPage,
        orderBy: 'trandate',
        orderByDirection: 'DESC'
      }).catch(() => {})
    }, 250),
    prevPage () {
      if (this.currentPage <= 1) return
      this.currentPage -= 1
      this.fetchPage()
    },
    nextPage () {
      if (!this.hasMoreOrders) return
      this.currentPage += 1
      this.fetchPage()
    },
    formatOrderType (type) {
      if (!type) return ''
      const labels = {
        CustInvc: 'Invoice',
        SalesReceipt: 'Sales Receipt',
        CashSale: 'Sales Receipt',
        Journal: 'Journal',
        CustPymt: 'Payment',
        CustCred: 'Credit Memo',
        CashRfnd: 'Cash Refund',
        Deposit: 'Deposit',
        SalesOrd: 'Sales Order'
      }
      return labels[type] || type
    },
    isOpeningOrder (order) {
      const value = order && order.is_opening_order
      return value === 1 || value === true || value === '1' || value === 'T'
    }
  }
}
</script>

<style lang="scss" scoped>
.order-pagination__select {
  @apply bg-grey-27 border-grey-21 normal-case;
  @screen lg {
    @apply py-1 bg-white;
  }
}

.skeleton-bone {
  @apply block;
  border-radius: 2px;
  height: 0.875rem;
  background: linear-gradient(
    90deg,
    #ebebeb 0%,
    #f7f7f7 40%,
    #ebebeb 80%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;

  &--sm {
    height: 0.75rem;
  }

  &--md {
    height: 1rem;
  }

  &--w-xs {
    width: 4.5rem;
  }

  &--w-sm {
    width: 6rem;
  }

  &--w-md {
    width: 7.5rem;
  }

  &--w-lg {
    width: 9rem;
  }
}

.skeleton-row {
  .skeleton-bone {
    animation-delay: calc(var(--i, 0) * 80ms);
  }

  &:nth-child(1) .skeleton-bone { --i: 0; }
  &:nth-child(2) .skeleton-bone { --i: 1; }
  &:nth-child(3) .skeleton-bone { --i: 2; }
  &:nth-child(4) .skeleton-bone { --i: 3; }
  &:nth-child(5) .skeleton-bone { --i: 4; }
  &:nth-child(6) .skeleton-bone { --i: 5; }
  &:nth-child(7) .skeleton-bone { --i: 6; }
  &:nth-child(8) .skeleton-bone { --i: 7; }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}
</style>
